<template>
  <el-dialog
    :title="isNew ? text.operatorAddAction : text.operatorEditAction"
    :visible="visible"
    width="520px"
    @update:visible="$emit('update:visible', $event)">
    <el-form label-width="110px" size="small">
      <el-form-item :label="text.operatorCodeLabel" :error="formError.code">
        <el-input v-model="form.code" class="operator-form__code" :disabled="!isNew" maxlength="8" clearable />
        <div class="operator-form__hint">{{ isNew ? text.operatorCodeHint : text.operatorCodeLocked }}</div>
      </el-form-item>
      <el-form-item :label="text.operatorNmLabel" :error="formError.operatorNm">
        <el-input v-model="form.operatorNm" class="operator-form__name" maxlength="60" clearable />
      </el-form-item>
      <el-form-item v-if="canSetHq" :label="text.hqYnLabel">
        <el-switch v-model="form.hqYn" class="operator-form__hq" active-value="Y" inactive-value="N" />
        <div class="operator-form__hint">{{ text.hqYnHint }}</div>
      </el-form-item>
      <el-form-item :label="text.mgrNmLabel" :error="formError.mgrNm">
        <el-input v-model="form.mgrNm" maxlength="30" clearable />
      </el-form-item>
      <el-form-item :label="text.mgrTelLabel" :error="formError.mgrTel">
        <el-input
          v-keep-number-caret
          :value="util.telFieldText(form.mgrTel)"
          maxlength="20"
          clearable
          @input="form.mgrTel = util.telDigits($event)" />
      </el-form-item>
      <el-form-item :label="text.memoLabel">
        <el-input v-model="form.memo" type="textarea" :rows="2" maxlength="200" />
      </el-form-item>
    </el-form>

    <!-- 서버가 거부한 이유(400·403·409)는 그 문장 그대로다 — 화면이 다시 쓰지 않는다 -->
    <p v-if="serverMessage" class="operator-form__error">{{ serverMessage }}</p>

    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ text.cancelAction }}</el-button>
      <el-button size="small" type="primary" :loading="saving" @click="save">{{ text.saveAction }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { HttpError, mergeErrors } from '@/plugins/http'
import { httpErrorMessage } from '@/plugins/http/policy'
import { CALLPOP_FORM_WRITE_CONFIG, operatorUrl } from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/**
 * 운영사 등록·수정 — 내부·콜팝 관리자만 부를 수 있는 두 API(`POST /callpop/operators`·`PUT …/{cd}`).
 *
 * **수정에는 `code` 를 싣지 않는다**(§3-2: 불변 — 관리점 코드의 앞 두 자라 바뀌면 발급한 코드가 거짓이 된다).
 * 코드 정규화(`cj` → `CJ`)와 총괄사 유일성은 **서버가 판정한다** — 화면은 그 400·409 문구를 그대로 보인다.
 */
export default {
  name: 'OperatorFormDialog',

  props: {
    visible: { type: Boolean, default: false },
    /** `null` 이면 등록, 행이 있으면 수정 */
    operator: { type: Object, default: null },
    /** 총괄사 플래그는 내부·콜팝 관리자만 만진다(§2-1 · 판정은 `callpopOperatorsMixin.operatorAbility`) */
    canSetHq: { type: Boolean, default: false },
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      form: this.initForm(null),
      formError: this.initFormError(),
      serverMessage: '',
      saving: false,
    }
  },

  computed: {
    isNew() {
      return !this.operator
    },
  },

  watch: {
    visible(open) {
      if (!open) return
      this.form = this.initForm(this.operator)
      this.formError = this.initFormError()
      this.serverMessage = ''
    },
  },

  methods: {
    initForm(row) {
      return {
        code: (row && row.code) || '',
        operatorNm: (row && row.operatorNm) || '',
        hqYn: (row && row.hqYn) || 'N',
        mgrNm: (row && row.mgrNm) || '',
        mgrTel: (row && row.mgrTel) || '',
        memo: (row && row.memo) || '',
      }
    },

    initFormError() {
      return { code: '', operatorNm: '', mgrNm: '', mgrTel: '' }
    },

    /**
     * 저장 본문 — 수정에서는 `code` 키 자체를 만들지 않는다(서버가 받지 않는 칸이다).
     *
     * **보이지 않은 칸의 키는 싣지 않는다**(플레이스 폼의 「부재 = 불변」과 같은 규칙): `hqYn` 은
     * [canSetHq] 일 때만 실린다. 서버는 총괄 지정 자격이 없는 요청의 `hqYn` 을 **현재 값과 다를 때** 막고
     * (§2-1), 키가 없으면 현재 값을 유지한다(appserver `02da2ffc` 확인).
     *
     * 「그 행의 현재 값을 돌려보낸다」도 계약을 만족하지만, 그러려면 화면이 행의 `hqYn` 을 정확히
     * 알아야 한다 — 값이 안 온 행에서는 `'N'` 을 **지어내** 총괄사가 자기 행을 고칠 때 403 이 된다.
     * 만질 수 없는 축은 말하지 않는 쪽이 짐작하지 않는다.
     */
    payload() {
      const body = {
        operatorNm: this.form.operatorNm,
        mgrNm: this.form.mgrNm,
        mgrTel: this.form.mgrTel,
        memo: this.form.memo || null,
      }
      if (this.canSetHq) body.hqYn = this.form.hqYn
      if (this.isNew) body.code = this.form.code

      return body
    },

    async save() {
      this.formError = this.initFormError()
      this.serverMessage = ''
      this.saving = true

      try {
        const url = this.isNew ? operatorUrl() : operatorUrl(this.operator.operatorCd)
        const response = this.isNew
          ? await this.$axios.post(url, this.payload(), CALLPOP_FORM_WRITE_CONFIG)
          : await this.$axios.put(url, this.payload(), CALLPOP_FORM_WRITE_CONFIG)

        this.$emit('saved', response.data)
        this.$emit('update:visible', false)
      } catch (error) {
        // 400 의 필드 오류는 칸에, 그 밖(403·409·메시지뿐인 400)은 폼 아래 한 줄로
        if (error instanceof HttpError && error.messages) mergeErrors(this.formError, error.messages)
        this.serverMessage = httpErrorMessage(error, this.text.requiredFieldMissing)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.operator-form__hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}
.operator-form__error {
  color: #f56c6c;
  font-size: 12px;
  margin: 0 0 4px;
}
</style>
